// owned by WP-TRUST · the Trust Centre's data (SPEC §4.3, §19): `/api/trust/*`, `/api/econic/rollout*` (this WP) and the key operations
// of WP-CRYPTO (`/api/kb/keys/status`, `/rotate`, `/api/kb/canary/run`). Ids, counts, states and fingerprints only: nothing here carries
// a key, a password, document text or a token.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useAuthMethods } from "@ncp/api-client";

export interface Bi { en: string; ar: string }

/** A load failure in words the screen can act on (the same shape as the account screens'). */
export type LoadProblem = "unavailable" | "forbidden" | "failed";

export function problemOf(error: unknown): LoadProblem | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.status === 404 || error.status === 405) return "unavailable";
    if (error.status === 403) return "forbidden";
  }
  return "failed";
}

const retry = (count: number, e: unknown) => problemOf(e) === "failed" && count < 1;

// ------------------------------------------------------------------ overview (security, encryption, ai, sources contributors)

export interface PostureCheck { key: string; state: "ok" | "warn" | "notice"; detail?: string | null }
export interface TrustPosture { state: "good" | "warn"; ok: boolean; checkedAt: string; checks: PostureCheck[] }

export interface TransportHop {
  key: "browser" | "edge" | "db" | "storage" | "sidecars" | "llm" | "binding" | string;
  state: "protected" | "off" | "exception" | "prepared" | "na";
  production: "protected" | "prepared" | "private-network" | "na";
  protected?: boolean | null;
  detail?: string | null;
  evidence?: string | null;
  exception?: string | null;
  leavesUae: boolean;
}
export interface PortBinding { service: string; port: number; state: "exposed" | "loopback-only" | "closed" }
export interface BindingProbe { checkedAt: string; ran: boolean; addresses: number; ports: PortBinding[] }

export interface SecuritySection {
  generatedAt: string;
  environment: { name: string; label: string; strict: boolean; preCutover?: boolean; exceptions: string[]; wouldRefuse?: string[] };
  authentication: {
    methods: Array<{ key: string; label: string; labelAr: string; enabled: boolean }>;
    mfaRequired: boolean;
    hash: string;
    totp: string;
    recoveryCodes: boolean;
    passwordPolicy: { minLength: number; maxLength: number; history: number; blocklist: string; expiryDays?: number | null };
    lockout: { threshold: number; firstLockMinutes: number; maxHours: number };
    sessions: { accessMinutes: number; idleMinutes: number; absoluteHours: number; revocationSeconds: number; maxPerUser: number; refresh: string };
    bypassCheck: { checkedAt: string; routesChecked: number; present: string[] };
    anonymousRoutes: { count: number; allowListVersion: number };
  };
  accounts: { active: number; withCredential: number; mfaEnrolled: number; awaitingFirstSignIn: number; locked: number; viewOnly: number; directoryLinked: number; localBootstrap: number };
  activity: { window: string; signIns: number; failedSignIns: number; lockouts: number; mfaFailures: number; refreshReuse: number; activeSessions: number };
  keys: Array<{ purpose: string; kid?: string | null; fingerprint?: string | null; algorithm?: string | null; source?: string | null; ageDays?: number | null; rotateEveryDays?: number | null; derivedFrom?: string | null }>;
  transport: Array<{ hop: string; protected: boolean; detail?: string; evidence?: string; exception?: boolean; leavesUae?: boolean }>;
  audit: { valid: boolean; events: number; headSequence?: number | null; headHash?: string | null; verifiedAt?: string; anchor?: { kind?: string; lastAt?: string | null; note?: string | null } | null };
  decisions: Array<{ key: string; status: string; since?: string }>;
}

export interface TradeOffCell { state: "encrypted" | "plaintext" | "hmac" | "none" | "na"; en: string; ar: string }
export interface EncryptionSection {
  generatedAt: string;
  kek: { id?: string | null; fingerprint?: string | null; source?: string | null; provider?: string | null; configured: boolean; required: boolean; rotatedAt?: string | null; known: Array<{ id: string; fingerprint: string; current: boolean }>; productionTarget?: string; productionTargetAr?: string; problems?: string[] };
  counts: { versions: number; encryptedObjects: number; keys: number; destroyed: number; encryptedChunks: number; legacyPlaintextChunks: number };
  legacyCorpus?: Bi | null;
  algorithms: { kek: string; dataKeys: string; files: string; fields: string };
  plaintextTradeOffs: Array<{ key: string; data: Bi; publicInternal: TradeOffCell; restricted: TradeOffCell; confidential: TradeOffCell }>;
  lastCanary?: { at?: string | null; ok: boolean; trigger?: string | null; mode?: string | null; db: string; objects: string; objectsScanned: number; tsvectorInternal: string; durationMs: number; failures: string[] } | null;
  selfTest?: { ok: boolean; at: string; ms: number } | null;
  dataProtection?: { enabled: boolean; protected: boolean; location?: string; keys: number; encryptedKeys?: number; plaintextKeys?: number; legacyPlaintextCopies?: number } | null;
  egress: { embeddings: string; embeddingsAr: string; zeroDataRetention: boolean; restricted: string; restrictedAr: string; confidential: string; confidentialAr: string };
  statements: Array<{ key: string; en: string; ar: string }>;
}

export interface AiSection {
  routes: Array<{ sensitivity: "public" | "internal" | "restricted" | "confidential"; tier: string; label: string; labelAr: string; documents: number }>;
  collections: Array<{ key: string; name: string; nameAr?: string | null; tier: string; encryptAtRest: boolean }>;
  zeroDataRetention: { chat: boolean; embeddings: boolean; provider: string };
  localAi: boolean;
  visionOcrForKb: boolean;
  restrictedTitles: boolean;
  policyVersion: number | string;
  agentMatrix: { version: number | string; rows: number };
}

export type SourceState = "live" | "updated" | "due" | "late" | "stale" | "degraded" | "blocked" | "upload-only" | "awaiting" | "model";
/** UAE official · international organisations · markets and prices · trade and logistics · news and research · the Ministry's own data. */
export type SourceCategory = "uae" | "international" | "markets" | "trade" | "news" | "ministry";
export const SOURCE_CATEGORIES: SourceCategory[] = ["uae", "international", "markets", "trade", "news", "ministry"];
export interface SourceStatusRow {
  sourceKey: string; name: string; nameAr: string; publisher: string; role: string; mode: string; state: SourceState;
  checkedAt?: string | null; nextCheckAt?: string | null; lastNewDataAt?: string | null; slaDays: number; ageDays?: number | null;
  nextReleaseExpected?: string | null; nextReleaseTitle?: string | null; lastError?: string | null; failuresInRow: number; licence: string; homepageUrl: string;
  /** The group the source belongs to, derived on the server from its key, publisher and mode (SourceCategories). */
  category?: SourceCategory | null;
}
export interface SourcesSection {
  asOf: string;
  egressEnabled: boolean;
  summary: { total: number; live: number; captured?: number; late?: number; stale?: number; degraded?: number; blocked?: number; due?: number; worst?: string | null; checkedAt?: string | null };
  sources: SourceStatusRow[];
  registry: Array<{ key: string; name: string; nameAr?: string | null; kind: string; status: string; signOff: "proposed" | "validated" | "signed-off" | "retired"; version: number; licence?: string | null; schedule?: string | null; enabled: boolean; owner?: string | null; publishedAt?: string | null; licensed: boolean; automatedAccessConfirmed?: boolean }>;
  allowedHosts: Array<{ host: string; sources: string[] }>;
  egress: { days: number; hosts: Array<{ host: string; requests: number; refused: number; failed: number; bytes: number; lastAt: string }> };
  blocked: Array<{ sourceKey: string; name: string; nameAr: string; state: string; mode: string; checkedAt?: string | null; lastError?: string | null; ask: string; askAr: string }>;
  licensed: Array<{ sourceKey: string; name: string; nameAr: string; licence: string; homepageUrl: string }>;
}

export interface TrustViewer { manage: boolean; keys: boolean; rollout: boolean; audit: boolean; accounts: boolean; knowledge: boolean }

export interface TrustOverview {
  generatedAt: string;
  posture: TrustPosture;
  sections: { security?: SecuritySection; encryption?: EncryptionSection; ai?: AiSection; sources?: SourcesSection };
  errors: Record<string, string>;
  transport: { hops: TransportHop[]; binding: BindingProbe };
  environment: string;
  /** The ten AI-governance rules (ECN-130) and the accessibility statement, from the server's catalogue. */
  governance: Array<{ number: number; rule: Bi; state: "in-place" | "partial" | "planned"; how: Bi }>;
  accessibility: Array<{ key: string; state: "supported" | "partial" | "known-gap"; title: Bi; detail: Bi }>;
  viewer: TrustViewer;
}

export const OVERVIEW_KEY = ["econic", "trust", "overview"] as const;

/**
 * The posture under the sign-in policy in force. The server's `mfa` check reads `Auth:RequireMfa`; ECONIC signs staff in with their
 * password alone (strict policy: 14+ characters, breached-password blocklist, lockout), so where no second factor is required there is
 * nothing for that check to find and it is left out; the state follows the remaining checks. A host that requires a second factor keeps
 * the check exactly as the server reports it.
 */
export function underSignInPolicy(posture: TrustPosture, mfaRequired: boolean | null | undefined): TrustPosture {
  if (mfaRequired !== false || !posture.checks.some((c) => c.key === "mfa")) return posture;
  const checks = posture.checks.filter((c) => c.key !== "mfa");
  const warn = checks.some((c) => c.state === "warn");
  return { ...posture, checks, state: warn ? "warn" : "good", ok: !warn };
}

const withPolicy = (d: TrustOverview): TrustOverview => {
  const posture = underSignInPolicy(d.posture, d.sections.security?.authentication.mfaRequired);
  return posture === d.posture ? d : { ...d, posture };
};

export function useTrustOverview() {
  const api = useApi();
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.get<TrustOverview>("/api/trust/overview"),
    select: withPolicy,
    staleTime: 20_000,
    retry,
  });
}

export function useRefreshOverview() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<TrustOverview>("/api/trust/overview?fresh=true"),
    onSuccess: (data) => qc.setQueryData(OVERVIEW_KEY, data),
  });
}

// ------------------------------------------------------------------ roadmap, inputs, success (this WP)

export interface Phase0Row { key: string; title: Bi; requirement: string; href: string; status: "done" | "in-progress" | "not-started" | "awaiting"; evidence: Bi; counts: Record<string, number> }
export interface DepartmentProgress { key: string; name: Bi; kind: string; state: "live" | "onboarding"; documents: number; lastAddedAt?: string | null; portal: boolean }
export interface SprintRow { key: string; letter: string; title: Bi; module?: string | null; priority: boolean; rollout: "everyone" | "groups" | "off" | "platform"; groups: string[]; live: boolean; next: Bi[] }
export interface MilestoneRow { key: string; title: Bi; fromMonth: number; toMonth: number; outcome: Bi; plannedFrom?: string | null; plannedTo?: string | null; actual?: string | null; note?: string | null; status: "reached" | "current" | "planned" }
export interface FeedbackCounts { new: number; inProgress: number; resolved: number; dismissed: number; total: number }
export interface RoadmapView {
  phase0: Phase0Row[];
  workstream1: { stages: Array<{ key: string; title: Bi }>; departments: DepartmentProgress[]; liveDepartments: number; onboarding: number; documents: number; inReview: number; connectorsPublished: number; connectorsLive: number; connectorsTotal: number };
  sprints: SprintRow[];
  milestones: MilestoneRow[];
  programmeStart?: string | null;
  currentMonth?: number | null;
  feedback: FeedbackCounts;
}

export interface InputRow { key: string; group: "technical" | "data" | "business"; question: Bi; why: Bi; owner: Bi; href?: string | null; status: "awaiting" | "answered" | "not-needed"; answer?: string | null; answeredBy?: string | null; updatedAt?: string | null }
export interface InputsView { total: number; awaiting: number; answered: number; items: InputRow[] }

export interface SuccessRow {
  key: string; area: string; title: Bi; comparator: "gte" | "lte" | "lt" | "eq"; target: number; proposedTarget: number; unit: string; method: Bi;
  value?: number | null; numerator?: number | null; denominator?: number | null; status: "met" | "not-met" | "not-measured" | "awaiting"; note?: Bi | null;
  targetEditedAt?: string | null; targetEditedBy?: string | null;
}
export interface SuccessView { at: string; met: number; measured: number; total: number; items: SuccessRow[] }

export function useRoadmap(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "roadmap"], queryFn: () => api.get<RoadmapView>("/api/trust/roadmap"), staleTime: 30_000, retry, enabled });
}

/** Questions that exist only while a second factor is required; with the password-only sign-in an unanswered one is moot and is not shown. */
const SECOND_FACTOR_INPUTS = new Set(["authenticator-resets"]);

export function inputsUnderSignInPolicy(view: InputsView, mfaRequired: boolean | null | undefined): InputsView {
  if (mfaRequired !== false) return view;
  const moot = view.items.filter((i) => SECOND_FACTOR_INPUTS.has(i.key) && i.status === "awaiting");
  if (!moot.length) return view;
  return { ...view, items: view.items.filter((i) => !moot.includes(i)), total: view.total - moot.length, awaiting: view.awaiting - moot.length };
}

export function useInputs(enabled = true) {
  const api = useApi();
  const mfaRequired = useAuthMethods()?.mfaRequired;
  return useQuery({
    queryKey: ["econic", "trust", "inputs"],
    queryFn: () => api.get<InputsView>("/api/trust/inputs"),
    select: (v: InputsView) => inputsUnderSignInPolicy(v, mfaRequired),
    staleTime: 30_000,
    retry,
    enabled,
  });
}

export function useSuccess(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "success"], queryFn: () => api.get<SuccessView>("/api/trust/success"), staleTime: 30_000, retry, enabled });
}

export function useProgrammeActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    for (const k of ["roadmap", "inputs", "success"]) void qc.invalidateQueries({ queryKey: ["econic", "trust", k] });
  };
  return {
    setInput: useMutation({ mutationFn: (v: { key: string; status: InputRow["status"]; answer?: string | null }) => api.put<InputRow>(`/api/trust/inputs/${encodeURIComponent(v.key)}`, { status: v.status, answer: v.answer ?? null }), onSuccess: refresh }),
    setMilestone: useMutation({ mutationFn: (v: { key: string; actual?: string | null; note?: string | null }) => api.put<MilestoneRow>(`/api/trust/roadmap/milestones/${encodeURIComponent(v.key)}`, { actual: v.actual ?? null, note: v.note ?? null }), onSuccess: refresh }),
    setTarget: useMutation({ mutationFn: (v: { key: string; target: number }) => api.put<SuccessRow>(`/api/trust/success/${encodeURIComponent(v.key)}`, { target: v.target }), onSuccess: refresh }),
  };
}

// ------------------------------------------------------------------ feedback (ECN-099)

export type FeedbackStatus = "New" | "InProgress" | "Resolved" | "Dismissed";
export type FeedbackCategory = "Wording" | "Layout" | "Error" | "Data" | "Suggestion" | "Other";
export interface FeedbackRow { id: string; createdAt: string; reporterName: string; mine: boolean; path: string; module: string; category: FeedbackCategory; text: string; locale: string; status: FeedbackStatus; note?: string | null; resolvedAt?: string | null; updatedAt: string }
export interface FeedbackList { counts: FeedbackCounts; items: FeedbackRow[]; all: boolean }

export function useFeedback(opts: { mine?: boolean; status?: FeedbackStatus | null; enabled?: boolean } = {}) {
  const api = useApi();
  const q = new URLSearchParams();
  if (opts.mine) q.set("mine", "true");
  if (opts.status) q.set("status", opts.status);
  return useQuery({
    queryKey: ["econic", "trust", "feedback", opts.mine ? "mine" : "all", opts.status ?? null],
    queryFn: () => api.get<FeedbackList>(`/api/trust/feedback${q.toString() ? `?${q}` : ""}`),
    staleTime: 15_000,
    retry,
    enabled: opts.enabled ?? true,
  });
}

export function useFeedbackActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["econic", "trust", "feedback"] });
    void qc.invalidateQueries({ queryKey: ["econic", "trust", "roadmap"] });
  };
  return {
    send: useMutation({
      mutationFn: (v: { path: string; category: FeedbackCategory; text: string; locale: string }) =>
        api.post<FeedbackRow>("/api/trust/feedback", { ...v, viewport: `${window.innerWidth}x${window.innerHeight}` }),
      onSuccess: refresh,
    }),
    setStatus: useMutation({ mutationFn: (v: { id: string; status: FeedbackStatus; note?: string | null }) => api.post<FeedbackRow>(`/api/trust/feedback/${v.id}/status`, { status: v.status, note: v.note ?? null }), onSuccess: refresh }),
  };
}

// ------------------------------------------------------------------ keys and canary (WP-CRYPTO; platform.keys.manage)

export interface KeysStatus {
  generatedAt: string; current?: string | null; fingerprint?: string | null; provider?: string | null; source?: string | null; problems?: string[];
  known: Array<{ id: string; fingerprint: string; current: boolean; keys: number }>;
  unknown: Array<{ id: string; keys: number }>;
  byPurpose: Array<{ purpose: string; live: number; destroyed: number }>;
  pendingRewrap: number;
  lastRotation?: { at: string; by?: string | null } | null;
  rotateSteps: Bi[];
}

export function useKeysStatus(enabled: boolean) {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "keys"], queryFn: () => api.get<KeysStatus>("/api/kb/keys/status"), staleTime: 30_000, retry, enabled });
}

export function useKeyActions() {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["econic", "trust", "keys"] });
    void qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
  };
  return {
    rotate: useMutation({ mutationFn: () => api.post<{ status?: string; note?: Bi } | null>("/api/kb/keys/rotate", {}), onSuccess: refresh }),
    canary: useMutation({ mutationFn: () => api.post<{ ok: boolean; summary?: unknown }>("/api/kb/canary/run", {}), onSuccess: refresh }),
  };
}

// ------------------------------------------------------------------ live sessions count (WP-SEC-API; platform.accounts.read)

export function useLiveSessionCount(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["econic", "admin", "sessions"],
    queryFn: () => api.get<unknown[]>("/api/trust/sessions"),
    select: (rows) => rows.length,
    staleTime: 10_000,
    retry,
    enabled,
  });
}

// ------------------------------------------------------------------ rollout (ECN-098)

export type RolloutMode = "everyone" | "groups" | "off";
export interface RolloutGroup { key: string; kind: "department" | "roles"; name: Bi }
export interface RolloutRow { module: string; mode: RolloutMode; groups: string[]; updatedAt?: string | null; updatedBy?: string | null }
export interface RolloutMatrix { version: number; groups: RolloutGroup[]; modules: RolloutRow[] }

export function useRolloutMatrix() {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "rollout"], queryFn: () => api.get<RolloutMatrix>("/api/econic/rollout/matrix"), staleTime: 10_000, retry });
}

export function useRolloutActions() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { module: string; mode: RolloutMode; groups: string[] }) => api.put<RolloutRow>(`/api/econic/rollout/${encodeURIComponent(v.module)}`, { mode: v.mode, groups: v.groups }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["econic", "trust", "rollout"] });
      void qc.invalidateQueries({ queryKey: ["econic", "rollout"] });
      void qc.invalidateQueries({ queryKey: ["econic", "trust", "roadmap"] });
    },
  });
}

/** Which modules the viewer sees (`GET /api/econic/rollout`); the shell and the hub hide the rest. */
export function useRollout() {
  const api = useApi();
  return useQuery({
    queryKey: ["econic", "rollout"],
    queryFn: () => api.get<{ version: number; modules: Array<{ module: string; visible: boolean; mode: RolloutMode }>; groups: string[] }>("/api/econic/rollout"),
    staleTime: 60_000,
    retry: false,
  });
}

// ------------------------------------------------------------------ audit view (ECN-105)

export interface AuditRow { sequence: number; id: string; occurredAt: string; actorKind: "Human" | "Agent" | "System"; actorUserId?: string | null; actorName: string; actorRole?: string | null; action: string; entityType: string; entityId?: string | null; severity: string; correlationId?: string | null; valid: boolean }
export interface AuditPage { rows: AuditRow[]; next?: number | null }
export interface AuditFacet { key: string; label?: string | null; count: number }
export interface AuditFacets { since: string; actions: AuditFacet[]; agents: AuditFacet[]; users: AuditFacet[]; entityTypes: AuditFacet[]; events: number }
export interface AuditChain { checkedAt: string; valid: boolean; events: number; tampered: number; brokenLinks: number; firstBroken?: number | null; headSequence?: number | null; headHash?: string | null; firstSequence?: number | null; gaps: number; elapsedMs: number }
export interface AuditFilter { user?: string; agent?: string; document?: string; action?: string; severity?: string }

export function useAuditEvents(filter: AuditFilter) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ["econic", "trust", "audit", "events", filter],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ take: "60" });
      for (const [k, v] of Object.entries(filter)) if (v) q.set(k, v);
      if (pageParam) q.set("before", String(pageParam));
      return api.get<AuditPage>(`/api/trust/audit/events?${q}`);
    },
    getNextPageParam: (last) => last.next ?? null,
    staleTime: 10_000,
    retry,
  });
}

export function useAuditFacets() {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "audit", "facets"], queryFn: () => api.get<AuditFacets>("/api/trust/audit/facets"), staleTime: 60_000, retry });
}

export function useAuditChain(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["econic", "trust", "audit", "chain"], queryFn: () => api.get<AuditChain>("/api/trust/audit/chain"), staleTime: 60_000, retry, enabled });
}

export function useRecheckChain() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<AuditChain>("/api/trust/audit/chain?fresh=true"),
    onSuccess: (data) => qc.setQueryData(["econic", "trust", "audit", "chain"], data),
  });
}
